import { useState } from 'react';
import {
  BookOpenIcon,
  BuildingLibraryIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  GlobeAltIcon,
  NewspaperIcon,
  SparklesIcon,
  UserCircleIcon,
  WrenchIcon,
} from '@heroicons/react/24/outline';

import { semanticColors } from '@/theme/tokens';
import VoteMateLogo from '@/ui-library/VoteMateLogo';

import { getFaviconUrl } from './SourceTypes';
import type { ToolCategory } from './NarrationTypes';

/**
 * The small circular badge beside each research step.
 *
 * **A source mark where the source is singular; a glyph for the kind of work.**
 *
 * The badge answers "where did this come from". Three categories are VoteMate's
 * own and carry the VoteMate logo:
 *
 * - `candidate` — proprietary policy data our own pipeline researches and writes.
 * - `ballot` — this voter's ballot and address as VoteMate holds them.
 * - `profile` — what the voter has told VoteMate about themselves.
 *
 * None of the three is an open-web lookup, and saying so is worth a badge:
 * "Looking up Steve Hilton…" beside our mark tells a voter this came from our
 * verified data rather than a search result.
 *
 * `legislative` and `ballotpedia` carry their source's own favicon, and `usafacts`
 * carries the USAFacts logo: every tool in those categories reads one publicly
 * identifiable source, so the mark is genuine provenance — the same way search
 * engines badge a result with the site's own icon. `usafacts` is the statistics
 * lookup, which reads government data through USAFacts' gateway. The favicons are
 * fetched through the favicon service the source chips use, deliberately not
 * artwork we ship: federal seals imply sanction in ways a flag-tile favicon does
 * not, and `public/assets/ballotpedia-logo.png` is a wide wordmark built for the
 * landing page's partner row, which cannot read at 22px in a circle. The USAFacts
 * logo is the exception — `public/assets/usafacts-logo.png` is already a circle,
 * so it fits the badge as is. If a favicon or the logo fails to load, that
 * category's glyph renders instead.
 *
 * `web` and `news` stay glyphs for the kind of work rather than a vendor mark:
 * a search spans many publishers, so no single icon would be provenance — and
 * `maf/executors/tool_status.py` bars status lines from naming the search
 * provider, which a Brave mark would reintroduce visually. The publishers a
 * search actually surfaced are disclosed where it matters: the citation chips
 * and the sources panel.
 */

// The first-party three keep an entry so the map stays exhaustive over
// ToolCategory — TypeScript then flags a new category that has no icon at all,
// and the glyph is what renders if a category ever leaves FIRST_PARTY_CATEGORIES.
const ICONS: Record<ToolCategory, typeof GlobeAltIcon> = {
  web: GlobeAltIcon,
  news: NewspaperIcon,
  legislative: BuildingLibraryIcon,
  ballotpedia: BookOpenIcon,
  usafacts: ChartBarIcon,
  candidate: UserCircleIcon,
  ballot: ClipboardDocumentCheckIcon,
  profile: SparklesIcon,
  generic: WrenchIcon,
};

/** Categories rendered with the VoteMate mark instead of a glyph. */
const FIRST_PARTY_CATEGORIES = new Set<ToolCategory>(['candidate', 'ballot', 'profile']);

/** Site favicon for a single-source category, keyed off the source's homepage. */
const CATEGORY_FAVICON_URLS: Partial<Record<ToolCategory, string | undefined>> = {
  legislative: getFaviconUrl('https://www.congress.gov'),
  ballotpedia: getFaviconUrl('https://ballotpedia.org'),
};

/** Bundled logo for a single-source category whose favicon is too small to read. */
const CATEGORY_LOGO_URLS: Partial<Record<ToolCategory, string>> = {
  usafacts: `${import.meta.env.BASE_URL}assets/usafacts-logo.png`,
};

/** Translation keys for each badge's accessible name. */
const LABEL_KEYS: Record<ToolCategory, string> = {
  web: 'narration.category.web',
  news: 'narration.category.news',
  legislative: 'narration.category.legislative',
  ballotpedia: 'narration.category.ballotpedia',
  usafacts: 'narration.category.usafacts',
  candidate: 'narration.category.candidate',
  ballot: 'narration.category.ballot',
  profile: 'narration.category.profile',
  generic: 'narration.category.generic',
};

export function toolCategoryLabelKey(category: ToolCategory): string {
  return LABEL_KEYS[category] ?? LABEL_KEYS.generic;
}

interface ToolCategoryIconProps {
  category: ToolCategory;
  size?: number;
}

/**
 * A circular badge with the category glyph.
 *
 * Purely decorative: the adjacent status line already says what is happening in
 * words, so the badge carries `aria-hidden` and the row owns the accessible
 * text. Announcing both would make a screen reader read every step twice.
 */
export default function ToolCategoryIcon({ category, size = 20 }: ToolCategoryIconProps) {
  // On favicon-load failure, fall back to the category glyph for good — the
  // service 404s consistently when it fails, so retrying per render only
  // repeats the request.
  const [faviconFailed, setFaviconFailed] = useState(false);
  const firstParty = FIRST_PARTY_CATEGORIES.has(category);
  const faviconUrl = faviconFailed
    ? undefined
    : (CATEGORY_LOGO_URLS[category] ?? CATEGORY_FAVICON_URLS[category]);
  const isLogo = category in CATEGORY_LOGO_URLS;
  const Icon = ICONS[category] ?? ICONS.generic;
  const glyph = Math.round(size * 0.6);
  const showSourceMark = firstParty || Boolean(faviconUrl);

  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: '50%',
        overflow: 'hidden',
        // Source marks are multi-colour, so they sit on the plain surface
        // rather than the tinted one the monochrome glyphs use — and the
        // contrast is deliberate: a named source should stand out from the
        // row of grey steps.
        backgroundColor: showSourceMark
          ? semanticColors.surface
          : semanticColors.backgroundSecondary,
        color: semanticColors.textCaption,
        border: `1px solid ${semanticColors.borderLight}`,
      }}
    >
      {firstParty ? (
        // Sized off the width so the logo's 0.886 aspect ratio stays centred in
        // the circle instead of being stretched to fill it.
        <VoteMateLogo size={Math.round(size * 0.66)} aria-hidden />
      ) : faviconUrl ? (
        <img
          src={faviconUrl}
          alt=""
          // A bundled logo is already a circle, so it fills the badge inside its border.
          width={isLogo ? size - 2 : Math.round(size * 0.7)}
          height={isLogo ? size - 2 : Math.round(size * 0.7)}
          style={{ display: 'block' }}
          onError={() => setFaviconFailed(true)}
        />
      ) : (
        <Icon style={{ width: glyph, height: glyph }} />
      )}
    </span>
  );
}
